import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { switchMap } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { normalizeBaseUrl } from '../utils/url.utils';
import { AuthService } from '../services/auth.service';

/**
 * Attaches access tokens to API requests.
 *
 * Responsibilities:
 * - only applies to backend API URLs
 * - adds `Authorization: Bearer ...` when a token is available
 * - adds e2e bypass header in e2e bypass mode
 *
 * Notes:
 * - this interceptor does not handle 401/403 responses
 * - auth error handling and reauthentication are handled in `authInterceptor`
 */
export const authTokenInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);

  const apiBaseUrl = normalizeBaseUrl(environment.apiBaseUrl);
  if (!req.url.startsWith(apiBaseUrl)) {
    return next(req);
  }

  if (environment.e2eAuthenticationBypass === true) {
    return next(
      req.clone({
        setHeaders: {
          'X-E2E-Bypass': 'true',
        },
      }),
    );
  }

  return authService.getAccessToken().pipe(
    switchMap((token) => {
      if (!token) {
        return next(req);
      }

      return next(
        req.clone({
          setHeaders: {
            Authorization: `Bearer ${token}`,
          },
        }),
      );
    }),
  );
};
